import { io, Socket } from 'socket.io-client';
import { runtimeConfig } from '../config/runtime';
import type { Notification } from '../contexts/NotificationContext';

export interface NotificationServiceEvents {
  notification: (notification: Notification) => void;
  // Phase C.38: cross-tab inbox state sync. Backend emits these
  // after markRead/clear so sibling tabs/devices reflect the change
  // immediately instead of waiting for the next periodic resync.
  'notification:read': (payload: { ids: string[] }) => void;
  'notification:cleared': (payload: { ids: string[] }) => void;
  connect: () => void;
  disconnect: () => void;
  // Phase C.97: import-monitor's real-time updates piggyback on the
  // same socket connection. `useImportData` registers handlers for
  // these via `notificationService.on(...)` after joining the
  // `import-monitor` room. Before this phase those .on() calls
  // silently dropped the handler into a Map that the service never
  // read — the events arrived on the underlying socket but the
  // service had no bridge from `socket.on('import-progress', …)` to
  // its own `emit('import-progress', …)` chain, so the real-time
  // path was effectively dead (polling fallback kept the page warm
  // and masked the breakage).
  'import-progress': (data: {
    jobId: string;
    status: string;
    progress: number;
    processedRecords?: number;
    totalRecords?: number;
    message?: string;
  }) => void;
  'import-status-change': (data: {
    jobId: string;
    status: string;
    message?: string;
  }) => void;
  'import-complete': (data: {
    jobId: string;
    results: unknown;
    message?: string;
  }) => void;
  'import-error': (data: { jobId: string; error: string }) => void;
  // Phase C.84: `error` event removed from the public surface. No
  // consumer ever registered for it, and the connect-error path
  // already rejects the `connect(token)` promise — which
  // `NotificationContext` actually consumes via `.catch` (Phase C.8
  // effect). The dropped event was just a parallel channel emitting
  // into the void.
  // Phase C.1: `ssr_alert` event deprecated and no longer emitted by
  // the backend. Phase C.19: `system_alert` likewise — the banner is
  // driven by ADD_NOTIFICATION promoting critical priority. Listeners
  // kept off the interface so consumers can't subscribe to dead
  // channels.
}

class NotificationService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private eventHandlers: Map<keyof NotificationServiceEvents, Function[]> = new Map();
  // Phase C.98: remember which rooms the caller asked us to join so
  // we can re-join after a reconnect. Before this phase the socket
  // recreation in `handleReconnection` (or any token-rotation
  // disconnect/connect cycle) dropped the user out of the
  // `import-monitor` room silently — useImportData calls
  // `joinRoom('import-monitor')` exactly once at mount, and there
  // was no path that re-emitted it for the freshly-built socket.
  private joinedRooms: Set<string> = new Set();
  // Phase C.100: handle for the pending reconnection setTimeout so
  // a caller-initiated `disconnect()` can cancel it. Without this,
  // logout-during-network-blip leaves the previously-scheduled
  // reconnect armed: 1s later it fires, reads the stale token from
  // localStorage (if logout didn't clear it fast enough), and
  // reconnects after the user expected to be signed out.
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.initializeEventHandlers();
  }

  private initializeEventHandlers() {
    // Initialize event handler maps
    const events: (keyof NotificationServiceEvents)[] = [
      'notification',
      'notification:read',
      'notification:cleared',
      'connect',
      'disconnect',
      'import-progress',
      'import-status-change',
      'import-complete',
      'import-error',
    ];
    
    events.forEach(event => {
      this.eventHandlers.set(event, []);
    });
  }

  public connect(token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Wait for current connection attempt
        const checkConnection = () => {
          if (this.socket?.connected) {
            resolve();
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
        return;
      }

      this.isConnecting = true;
      const authToken = token || localStorage.getItem('auth_token');

      if (!authToken) {
        this.isConnecting = false;
        reject(new Error('No authentication token available'));
        return;
      }

      try {
        // Phase C.101: dispose of any prior socket before assigning
        // the new one. `forceNew: true` makes io() build a fresh
        // Manager + Socket every call, but the old socket object
        // wasn't being explicitly torn down — its listeners stayed
        // wired and routed events through `this.emit()` (same
        // singleton service, same handler Map) until GC. Mid-reconnect
        // we could briefly have two live sockets feeding duplicate
        // 'notification' events into the reducer. Remove-then-
        // disconnect order matters: stripping listeners first means
        // the synchronous 'disconnect' event the old socket fires
        // has nowhere to land (no double-handleReconnection).
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
        // Add fallback transports for better compatibility
        this.socket = io(runtimeConfig.apiUrl, {
          auth: { token: authToken },
          transports: ['websocket', 'polling'],
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          forceNew: true,
          upgrade: true,
        });

        this.setupSocketEventListeners();

        this.socket.on('connect', () => {
          console.log('Notification service connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          // Phase C.98: re-join every previously-joined room on the
          // freshly-built socket. The server-side rooms live on the
          // socket instance, so a reconnect (network blip,
          // token-rotation, handleReconnection) leaves us out of
          // every room until we re-emit `join_room`.
          for (const room of this.joinedRooms) {
            this.socket?.emit('join_room', room);
          }
          this.emit('connect');
          resolve();
        });

        this.socket.on('connect_error', (error) => {
          console.error('Notification service connection error:', error);
          this.isConnecting = false;
          // Phase C.84: only the connect-promise rejection is observed
          // by callers (NotificationContext's `.catch` in the C.8
          // effect). No public `error` event needed.
          reject(error);
        });

      } catch (error) {
        this.isConnecting = false;
        console.error('Failed to create socket connection:', error);
        reject(error);
      }
    });
  }

  private setupSocketEventListeners() {
    if (!this.socket) return;

    this.socket.on('disconnect', (reason) => {
      console.log('Notification service disconnected:', reason);
      this.emit('disconnect');

      // Phase C.100: two reasons skip reconnection. Both are
      // intentional terminations, not failures we should retry:
      //   - 'io server disconnect' — server kicked us
      //   - 'io client disconnect' — our own `disconnect()` ran
      //     (logout, token rotation cleanup, dev HMR teardown).
      //     Without this guard, logout would schedule a reconnect
      //     that fires after the user expected to be signed out.
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        return;
      }

      this.handleReconnection();
    });

    this.socket.on('notification', (notification: Notification) => {
      console.log('Received notification:', notification);
      this.emit('notification', notification);
    });

    // Phase C.38: cross-tab state sync — forward to context handlers.
    this.socket.on('notification:read', (payload: { ids: string[] }) => {
      this.emit('notification:read', payload);
    });
    this.socket.on('notification:cleared', (payload: { ids: string[] }) => {
      this.emit('notification:cleared', payload);
    });

    // Phase C.97: bridge import-monitor real-time events. Backend
    // emits these from `socketEmitter` to the `import-monitor` room
    // (which `useImportData` joins via `joinRoom('import-monitor')`).
    // The service runs as a singleton on one socket connection so
    // multiplexing import-* + notification events through the same
    // pipe is intentional — separate sockets per concern would
    // duplicate the JWT handshake cost and confuse the connection
    // status indicator.
    this.socket.on('import-progress', (data: Parameters<NotificationServiceEvents['import-progress']>[0]) => {
      this.emit('import-progress', data);
    });
    this.socket.on('import-status-change', (data: Parameters<NotificationServiceEvents['import-status-change']>[0]) => {
      this.emit('import-status-change', data);
    });
    this.socket.on('import-complete', (data: Parameters<NotificationServiceEvents['import-complete']>[0]) => {
      this.emit('import-complete', data);
    });
    this.socket.on('import-error', (data: Parameters<NotificationServiceEvents['import-error']>[0]) => {
      this.emit('import-error', data);
    });

    // Phase C.19: `system_alert` socket channel removed. Backend
     // never emitted it (grep confirms zero call sites). The frontend
     // banner shown for critical alerts is now driven entirely by
     // `ADD_NOTIFICATION` reducer promoting `priority === 'critical'`
     // into `state.systemAlert` (Phase C.12) — same UX, no dead wire.

    // Phase C.44: `import_job_update` socket channel removed —
    // backend never emitted this event. The actual import lifecycle
    // emit names are `import-progress` / `import-status-change` /
    // `import-complete` / `import-error` (dashes, not underscores)
    // and they go to the `import-monitor` room, consumed by
    // `useImportData` for that page. Persistent inbox rows for
    // job complete/error are created by `socketEmitter`'s
    // `writeImportInboxRow` (Phase C.3) which emits the unified
    // `notification` event — already handled above.

    // Phase C.1: `ssr_alert` socket channel deprecated. Backend now
    // emits the unified `notification` event for SSR alerts (via
    // `inboxNotificationService.createNotificationsForUsers`). The
    // listener is gone — old payloads will be silently dropped.

    // Phase C.43: `user_notification` socket channel removed.
    // Backend never emitted it (`grep` confirms zero call sites);
    // the listener fabricated synthetic ids client-side which would
    // never reach DB anyway. Per-user notifications now flow through
    // the unified `notification` event via
    // `inboxNotificationService.createNotification` (with persistence).
  }

  // Phase C.44: `getNotificationTypeFromJobStatus` removed — only
  // used by the dead `import_job_update` handler. Type mapping
  // for import jobs now lives backend-side in
  // `socketEmitter.writeImportInboxRow` (Phase C.3) — single
  // source of truth.

  private handleReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    // Phase C.100: store the handle so `disconnect()` can cancel a
    // pending retry that was queued before the user logged out.
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      const token = localStorage.getItem('auth_token');
      if (token) {
        this.connect(token).catch(error => {
          console.error('Reconnection failed:', error);
        });
      }
    }, delay);
  }

  public disconnect(): void {
    // Phase C.100: cancel any in-flight reconnection schedule first.
    // Otherwise a network-blip retry queued seconds ago will still
    // fire after `disconnect()` returns, reading the stale token
    // out of localStorage and silently reconnecting the user.
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  public on<K extends keyof NotificationServiceEvents>(
    event: K,
    handler: NotificationServiceEvents[K]
  ): void {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.push(handler);
    this.eventHandlers.set(event, handlers);
  }

  public off<K extends keyof NotificationServiceEvents>(
    event: K,
    handler: NotificationServiceEvents[K]
  ): void {
    const handlers = this.eventHandlers.get(event) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      this.eventHandlers.set(event, handlers);
    }
  }

  private emit<K extends keyof NotificationServiceEvents>(
    event: K,
    ...args: Parameters<NotificationServiceEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.forEach(handler => {
      try {
        (handler as any)(...args);
      } catch (error) {
        console.error(`Error in notification service event handler for ${event}:`, error);
      }
    });
  }

  // Phase C.43: `sendNotification` removed — never called externally
  // and the underlying `send_notification` socket event had no
  // backend handler. To create a notification server-side, use the
  // REST POST flow + `inboxNotificationService.createNotification`
  // (per-user persistence + Socket.IO push via the helper).

  // Phase C.19: `sendSystemAlert` removed — never called anywhere
  // and the underlying socket channel (`send_system_alert` →
  // backend → `system_alert`) had no backend handler either. To
  // surface a banner now, write a `priority: 'critical'`
  // notification via `inboxNotificationService.createNotification`
  // server-side; Phase C.12 reducer auto-promotes it.

  // Join notification rooms (for targeted notifications).
  // Phase C.98: remember the room in `joinedRooms` so we can re-emit
  // on every reconnect. Idempotent — repeated joinRoom() calls for
  // the same room don't pile up server-side (socket.io's join is
  // also idempotent) and the Set ensures we only track it once.
  public joinRoom(room: string): void {
    this.joinedRooms.add(room);
    if (this.socket?.connected) {
      this.socket.emit('join_room', room);
    }
  }

  public leaveRoom(room: string): void {
    this.joinedRooms.delete(room);
    if (this.socket?.connected) {
      this.socket.emit('leave_room', room);
    }
  }

  // Get connection status
  // Phase C.49: `getConnectionInfo` removed — never called.
  // Connection state is observable through `isConnected()` and the
  // `connect` / `disconnect` socket events; the structured debug
  // bundle had no consumers.
}

// Create singleton instance
export const notificationService = new NotificationService();

// Auto-connect when auth token is available
const initializeNotificationService = () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (token && !notificationService.isConnected()) {
      notificationService.connect(token).catch(error => {
        console.error('Failed to initialize notification service:', error);
      });
    }
  } catch (error) {
    console.error('Error during notification service initialization:', error);
  }
};

// Phase C.64: cross-tab token change listener stays — Redux state
// doesn't sync across tabs by default, so this is the only path
// that disconnects tab B's socket when tab A logs out. The initial
// setTimeout(100ms) auto-connect was removed: NotificationContext's
// Phase C.8 effect (deps `[isAuthenticated]`) is the canonical
// connect-on-auth path and runs deterministically after React mount,
// no race window with the module-level timeout.
if (typeof window !== 'undefined') {
  try {
    window.addEventListener('storage', (event) => {
      if (event.key === 'auth_token') {
        if (event.newValue) {
          initializeNotificationService();
        } else {
          notificationService.disconnect();
        }
      }
    });
  } catch (error) {
    console.error('Error setting up notification service listeners:', error);
  }
}

export default notificationService;
